/* eslint-disable consistent-return */
/* eslint-disable jsx-a11y/interactive-supports-focus */
/* eslint-disable jsx-a11y/click-events-have-key-events */
/* eslint-disable react/no-unknown-property */
import React from 'react';
import {
  Icon, ICON_ARROW_UP, Spacing, Stack, Text,
} from '@doveit/bricks';
import * as styles from './SimplePageLayout.style';
import useIsDevice from '../../hooks/use-is-device/useIsDevice';
import { SimplePageLayoutWrapperSize } from './types';

export interface SimplePageLayoutProps extends React.AriaAttributes, React.PropsWithChildren {
  pageTitle?: string | React.ReactNode,
  pageDescription?: string | React.ReactNode,
  header?: React.ReactNode,
  toolbar?: React.ReactNode,
  footer?: React.ReactNode,
  boxed?: boolean,
  fitMaxAvailableHeight?: boolean,
  wrapperSize?: SimplePageLayoutWrapperSize,
}

const HEADER_HIDE_THRESHOLD = 60;
const BACK_TO_TOP_THRESHOLD = 200;

const SimplePageLayout: React.FC<SimplePageLayoutProps> = ({
  pageTitle,
  pageDescription,
  header,
  toolbar,
  children,
  wrapperSize = 'L',
  fitMaxAvailableHeight,
  boxed = false,
  footer,
  ...rest
}) => {
  const scrollRef = React.useRef<HTMLDivElement>(null);
  const [hideHeader, setHideHeader] = React.useState(false);
  const [showBackToTop, setShowBackToTop] = React.useState(false);
  const isMobile = useIsDevice('mobile');
  const enableHideHeader = !!header && !!toolbar;

  const handleScroll = React.useCallback(() => {
    const scrollElement = scrollRef.current;
    if (!scrollElement) return;

    if (enableHideHeader) {
      setHideHeader(scrollElement.scrollTop >= HEADER_HIDE_THRESHOLD);
    }

    if (isMobile) {
      setShowBackToTop(scrollElement.scrollTop >= BACK_TO_TOP_THRESHOLD);
    }
  }, [enableHideHeader, isMobile]);

  React.useEffect(() => {
    const scrollElement = scrollRef.current;
    if (!scrollElement) return;

    scrollElement.addEventListener('scroll', handleScroll);

    return () => {
      scrollElement.removeEventListener('scroll', handleScroll);
    };
  }, [handleScroll]);

  const scrollToTop = React.useCallback(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = 0;
    }
  }, []);

  return (
    <div
      {...rest}
      css={styles.base}
    >
      {header && (
        <div
          css={styles.header}
          $hideHeader={hideHeader}
        >
          <div>
            <div
              css={styles.wrapper}
              $size={wrapperSize}
            >
              {header}
            </div>
          </div>
        </div>
      )}
      {toolbar && (
        <div css={styles.bar}>
          <div
            css={styles.wrapper}
            $size={wrapperSize}
          >
            {toolbar}
          </div>
        </div>
      )}
      <div
        css={styles.content}
        fitMaxAvailableHeight={fitMaxAvailableHeight}
      >
        <div
          css={styles.main}
          ref={scrollRef}
          $fitMaxAvailableHeight={fitMaxAvailableHeight}
        >
          <div
            css={styles.wrapper}
            $size={wrapperSize}
            $fitMaxAvailableHeight={fitMaxAvailableHeight}
          >
            <div css={boxed ? styles.frame : undefined}>
              {(pageTitle || pageDescription) && (
                <Spacing margin={[0, 0, 300]}>
                  <Stack gap={150}>
                    {typeof pageTitle === 'string'
                      ? <Text.H3 as="h1">{pageTitle}</Text.H3>
                      : pageTitle}
                    {typeof pageDescription === 'string'
                      ? <Text.Body>{pageDescription}</Text.Body>
                      : pageDescription}
                  </Stack>
                </Spacing>
              )}
              {children}
            </div>
          </div>
          <div
            role="button"
            aria-label="Torna all'inizio pagina"
            css={styles.backToTop}
            onClick={scrollToTop}
            $visible={showBackToTop}
          >
            <Icon size={32} path={ICON_ARROW_UP} />
          </div>
        </div>
      </div>
      {footer && (
        <div css={styles.bar}>
          <div
            css={styles.wrapper}
            $size={wrapperSize}
          >
            {footer}
          </div>
        </div>
      )}
    </div>
  );
};

export default SimplePageLayout;
